import { Desktop } from '@99designs/common/components/Desktop';
import { Mobile } from '@99designs/common/components/Mobile';
import { colors, spaces, typography } from '@99designs/common/core/settings';
import { DesignerMeta_User } from '__generated__/DesignerMeta_User';
import Avatar from 'projects/components/Avatar';
import LocalTime from 'projects/components/LocalTime';
import OnlineStatus from 'projects/components/OnlineStatus';
import { isDesignerOnline } from 'projects/util/designer';
import * as React from 'react';
import styled from 'styled-components';

const StyledDesignerMetaHeader = styled.div`
    grid-area: header;
    display: grid;
    grid-template-columns: auto auto 1fr;
    grid-template-areas:
        'avatar name name name'
        'avatar online-status local-time local-time';

    padding-bottom: ${spaces.space6}px;
`;

const StyledDesignerMetaHeaderNarrow = styled(StyledDesignerMetaHeader)`
    grid-template-areas:
        'avatar name name name'
        'avatar online-status online-status online-status'
        'avatar local-time local-time local-time';
`;

const AvatarWrapper = styled.div`
    grid-area: avatar;
    padding-right: ${spaces.space7}px;
    align-self: center;
`;

const DesignerName = styled.div`
    font-weight: bold;
    grid-area: name;
    align-self: end;
`;

const StyledOnlineStatus = styled.div`
    grid-area: online-status;
    line-height: ${typography.lineHeights.root};
    font-size: ${typography.typeScale.size2}px;
    text-align: left;
    align-self: center;
    margin-right: ${spaces.space6}px;
`;

const StyledLocalTime = styled.div`
    grid-area: local-time;
    align-self: end;
    line-height: ${typography.lineHeights.root};
    font-size: ${typography.typeScale.size2}px;
    color: ${colors.fiftyFirstShade};
`;

interface DesignerMetaHeaderBaseProps {
    designer: DesignerMeta_User;
    onRemove?: (userId: number) => void;
    paragraphSize: 'regular' | 'small';
    avatarSize: 'small' | 'extra-small';
}

function DesignerMetaHeaderBase({
    designer,
    onRemove,
    paragraphSize,
    avatarSize,
}: DesignerMetaHeaderBaseProps) {
    const isOnlineNow = isDesignerOnline(designer);

    return (
        <>
            <AvatarWrapper>
                <Avatar
                    userId={designer.id}
                    variations={[avatarSize]}
                    isOnline={isOnlineNow}
                    onRemove={onRemove}
                />
            </AvatarWrapper>
            <DesignerName data-test-id="designer-meta-header-name">
                <div className="paragraph paragraph--regular paragraph--no-margin">
                    {designer.displayName}
                </div>
            </DesignerName>
            <StyledOnlineStatus>
                <div className={`paragraph paragraph--no-margin paragraph--${paragraphSize}`}>
                    <OnlineStatus isOnline={isOnlineNow} lastSeenAt={designer.profile.lastSeenAt} />
                </div>
            </StyledOnlineStatus>
            <StyledLocalTime>
                <div className={`paragraph paragraph--no-margin paragraph--${paragraphSize}`}>
                    <LocalTime utcOffset={designer.profile.utcOffset} />
                </div>
            </StyledLocalTime>
        </>
    );
}

export default function DesignerMetaHeader({ ...props }: DesignerMetaHeaderBaseProps) {
    return (
        <>
            <Desktop>
                <DesignerMetaHeaderStandard {...props} />
            </Desktop>
            <Mobile>
                <DesignerMetaHeaderNarrow {...props} />
            </Mobile>
        </>
    );
}

export const DesignerMetaHeaderStandard = (props: DesignerMetaHeaderBaseProps) => (
    <StyledDesignerMetaHeader>
        <DesignerMetaHeaderBase {...props} />
    </StyledDesignerMetaHeader>
);

export const DesignerMetaHeaderNarrow = (props: DesignerMetaHeaderBaseProps) => (
    <StyledDesignerMetaHeaderNarrow>
        <DesignerMetaHeaderBase {...props} />
    </StyledDesignerMetaHeaderNarrow>
);
